import { Link, useParams } from 'react-router'
import { Building } from 'lucide-react'
import { EmptyState, PageHeader } from '@/components/composites/states'
import { Skeleton } from '@/components/ui/skeleton'
import { paths } from '@/routes/paths'
import { useTick } from '@/features/worktime/api'
import { useTeamDay } from './api'
import { PersonHeader, PersonStatus } from './PersonStatus'
import { TargetEditor } from './TargetEditor'

/**
 * Who is on what, right now. The same numbers as the office floor, in a list: what each person is
 * timing, how long they have worked today, and the target they set themselves for the day.
 */
export function TeamDayPage() {
  const { workspaceSlug = '' } = useParams()
  const team = useTeamDay()
  const data = team.data
  const anyoneWorking = Boolean(data?.people.some((p) => p.working))
  const now = useTick(anyoneWorking)
  const me = data?.people.find((p) => p.userId === data.me)
  const working = data?.people.filter((p) => p.working).length ?? 0

  return (
    <div className="mx-auto w-full max-w-[1000px] px-2 pb-10 md:px-6">
      <PageHeader
        kicker="Team"
        title="Team today"
        description="What everyone is working on right now, how long they have been at it today, and what they are aiming for."
        actions={
          <Link to={paths.office(workspaceSlug)} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium hover:bg-muted">
            <Building className="size-4" /> Walk the office
          </Link>
        }
      />

      {data ? (
        <section className="mb-5 rounded-lg border border-border bg-card p-4">
          <TargetEditor key={data.today + (me?.target?.text ?? '') + (me?.target?.minutes ?? '')} current={me?.target ?? null} />
          {me && !me.working ? (
            <p className="mt-3 text-xs text-text-muted">
              You are not timing anything. Open a work item you are on and press <strong>Start work</strong> so the team can see it.
            </p>
          ) : null}
        </section>
      ) : null}

      {team.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : team.isError ? (
        <p className="text-sm text-danger">The team could not be loaded. Try again in a moment.</p>
      ) : !data || data.people.length === 0 ? (
        <EmptyState title="Nobody here yet" description="People appear once they are members of the organisation." />
      ) : (
        <>
          <p className="mb-2 text-xs text-text-muted">
            {working === 0 ? 'Nobody is timing work right now.' : `${working} of ${data.people.length} working right now.`} Updates every few seconds.
          </p>
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.people.map((person) => (
              <li key={person.userId} className="grid gap-3 rounded-lg border border-border bg-card p-3">
                <PersonHeader person={person} />
                <PersonStatus person={person} fetchedAt={team.data.fetchedAt} now={now} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
